import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatMenu, MatMenuContent, MatMenuTrigger } from '@angular/material/menu';
import { PagerLabelFormat } from '@common/enums/enums';

@Component({
    selector: 'st-pager',
    imports: [MatIcon, MatIconButton, MatListOption, MatMenu, MatMenuContent, MatMenuTrigger, MatSelectionList],
    templateUrl: './pager.html',
    styleUrl: './pager.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-pager',
    },
})
export class PagerComponent {
    readonly PagerLabelFormat = PagerLabelFormat;

    public readonly startIndex = signal(0);
    readonly startIndexChange = output<number>();

    readonly labelFormat = input<PagerLabelFormat>(PagerLabelFormat.Range);
    readonly showFirstLast = input(false);

    readonly pageSize = input(7, {
        transform: (value: NumberInput) => {
            return coerceNumberProperty(value);
        },
    });

    readonly totalCount = input(0, {
        transform: (value: NumberInput) => {
            if (value !== this.totalCount()) {
                this.startIndex.set(0);
                this.startIndexChange.emit(this.startIndex());
            }
            return coerceNumberProperty(value);
        },
    });

    readonly prevDisabled = computed(() => {
        return this.startIndex() === 0;
    });

    readonly nextDisabled = computed(() => {
        return this.startIndex() + this.visibleItemsCount() === this.totalCount();
    });

    readonly label = computed(() => {
        if (this.labelFormat() === PagerLabelFormat.Page) {
            const currentPage = Math.floor(this.startIndex() / this.pageSize()) + 1;
            const totalPages = Math.ceil(this.totalCount() / this.pageSize()) || 0;
            return `Page ${currentPage} of ${totalPages}`;
        }
        return `${this.startIndex() + 1} - ${this.startIndex() + this.visibleItemsCount()} of ${this.totalCount()}`;
    });

    readonly totalPages = computed(() => Math.ceil(this.totalCount() / this.pageSize()) || 0);

    readonly pagesList = computed(() => {
        return Array.from({ length: this.totalPages() }, (_, i) => i + 1);
    });

    readonly currentPage = computed(() => Math.floor(this.startIndex() / this.pageSize()) + 1);

    readonly visibleItemsCount = computed(() => {
        let count = this.pageSize();
        if (this.totalCount() < this.pageSize()) {
            count = this.totalCount();
        } else if (this.totalCount() < count) {
            count = this.totalCount();
        } else if (this.totalCount() < this.startIndex() + this.pageSize()) {
            count = this.totalCount() - this.startIndex();
        }
        return count;
    });

    prev() {
        if (this.prevDisabled()) {
            return;
        }
        // 1. move to prev page
        // new start index becomes startIndex - pageSize
        this.startIndex.set(this.startIndex() - this.pageSize());
        // 2. update timeline data
        this.startIndexChange.emit(this.startIndex());
    }

    next() {
        if (this.nextDisabled()) {
            return;
        }
        // 1. move to next page
        // new start index becomes startIndex + pageSize
        this.startIndex.set(this.startIndex() + this.pageSize());
        // 2. update timeline data
        this.startIndexChange.emit(this.startIndex());
    }

    first() {
        if (this.prevDisabled()) {
            return;
        }
        this.startIndex.set(0);
        this.startIndexChange.emit(this.startIndex());
    }

    last() {
        if (this.nextDisabled()) {
            return;
        }
        const lastPageStart = Math.floor((this.totalCount() - 1) / this.pageSize()) * this.pageSize();
        this.startIndex.set(lastPageStart);
        this.startIndexChange.emit(this.startIndex());
    }

    goToPage(event: MatSelectionListChange) {
        const page = event.options[0].value as number;
        this.startIndex.set(page * this.pageSize());
        this.startIndexChange.emit(this.startIndex());
    }
}
